import React, { FC } from 'react';
import { Platform, ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';
import { LeftColumnProps } from '@components/ncFileVersions/types';
import { scale } from '@components/frame';
import { LEFT_CENTRAL_BLOCK_WIDTH } from '@components/ncFileVersions/constants';
import { isPhoneNative } from '@helpers';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { PADDING_HORIZONTAL_FOR_SHADOW, SCREEN_BORDER_RADIUS, SCREEN_PADDING_VERTICAL_FOR_ITEM, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import NCFileVersionSelector from '@components/ncFileVersions/components/NCFileVersionSelector';
import ActionButton from '@components/ncFileVersions/components/ActionButton';
import CompareVersionsBlock from '@components/ncFileVersions/components/CompareVersionsBlock';
import { useManageLeftColumn } from '@components/ncFileVersions/components/LeftColumn/hooks/useManageLeftColumn';

const LeftColumn: FC<LeftColumnProps> = ({
  C,
  favoriteOrLatest,
  favorite,
  selectedVersion,
  forceFullWidth = false,
  machineWhichRanFavoriteOrLatest,
  otherVersions,
  shouldDisplayFilterInactiveVersionsButton,
  showOnlyActiveVersions,
  setShowOnlyActiveVersions,
  inactiveVersionsCount = 0,
  onSelectVersion,
  comparisonMode = false,
  versionsToCompare = [],
  onToggleVersionForComparison,
  onToggleFavoriteVersion,
  onUploadNewVersion,
  showCompareCardInline = false,
  onEnterComparisonMode,
  scrollStyle,
}) => {
  const { shadowStyles, toggleSort, sortedOtherVersions, handleVersionClick, isSelected, favoriteCaption, hasOtherVersions, showSortButton, showInactiveToggle, inactiveToggleText } =
    useManageLeftColumn({
      C,
      favorite,
      otherVersions,
      comparisonMode,
      versionsToCompare,
      selectedVersion,
      onSelectVersion,
      onToggleVersionForComparison,
      machineWhichRanFavoriteOrLatest,
      shouldDisplayFilterInactiveVersionsButton,
      showOnlyActiveVersions,
      inactiveVersionsCount,
    });

  return (
    <View style={[forceFullWidth || isPhoneNative ? { flex: 1, width: '100%' } : { width: scale(LEFT_CENTRAL_BLOCK_WIDTH) }]}>
      <ScrollView showsVerticalScrollIndicator={false} style={scrollStyle} contentContainerStyle={{ paddingBottom: scale(10) }}>
        {favoriteOrLatest && (
          <View style={{ position: 'relative' }}>
            <NCFileVersionSelector
              {...{
                C,
                version: favoriteOrLatest,
                selected: isSelected(favoriteOrLatest),
                isFavorite: !!favorite,
                onPress: handleVersionClick,
                onToggleFavorite: onToggleFavoriteVersion,
                withShadow: true,
                withFavoriteIcon: true,
                withSelectionIndicator: comparisonMode,
              }}
            />
          </View>
        )}

        {favoriteOrLatest && (
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(15), marginTop: scale(2) }]}>{favoriteCaption}</Text>
        )}

        {hasOtherVersions && (
          <>
            <View
              style={{
                marginTop: scale(22),
                paddingHorizontal: scale(18),
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: scale(24),
              }}
            >
              <Text style={[font.Headline, { color: C('Label/Light/Primary'), fontWeight: 500 }]}>{i18n.t('Other Versions')}</Text>
              {showSortButton && (
                <TouchableElement onPress={toggleSort}>
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: scale(6),
                          paddingHorizontal: scale(8),
                          paddingVertical: scale(4),
                          borderRadius: scale(999),
                          backgroundColor: C(pressHoverColor('Background/Light/Detail')),
                        },
                      ]}
                    >
                      <Text
                        style={{
                          fontSize: scale(13),
                          lineHeight: scale(19),
                          fontWeight: '400',
                          color: C('Link/Light/Primary'),
                          fontFamily: Platform.OS === 'web' ? 'Roboto' : undefined,
                        }}
                      >
                        {i18n.t('Sort')}
                      </Text>
                      <Svg icon="updown" width={scale(16)} height={scale(16)} color="Link/Light/Primary" />
                    </View>
                  )}
                </TouchableElement>
              )}
            </View>

            <View
              style={{
                marginTop: scale(5),
                paddingHorizontal: scale(PADDING_HORIZONTAL_FOR_SHADOW),
              }}
            >
              <View
                style={[
                  shadowStyles,
                  {
                    borderRadius: scale(SCREEN_BORDER_RADIUS),
                    backgroundColor: C('Background/Light/Field'),
                  },
                ]}
              >
                <View style={{ borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' }}>
                  <View
                    style={{
                      paddingVertical: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
                    }}
                  >
                    {sortedOtherVersions.map((version, index) => (
                      <View key={version?.ncFileVersionId} style={{ position: 'relative' }}>
                        <NCFileVersionSelector
                          {...{
                            C,
                            isFavorite: false,
                            selected: isSelected(version),
                            withSelectionIndicator: comparisonMode,
                            version,
                            roundTop: index === 0,
                            roundBottom: index === sortedOtherVersions.length - 1,
                            bottomLine: index !== sortedOtherVersions.length - 1,
                            onPress: handleVersionClick,
                            onToggleFavorite: onToggleFavoriteVersion,
                          }}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          </>
        )}

        {showCompareCardInline && !comparisonMode && onEnterComparisonMode && (
          <View style={{ marginTop: scale(24), paddingHorizontal: scale(PADDING_HORIZONTAL_FOR_SHADOW) }}>
            <CompareVersionsBlock C={C} selectedVersion={selectedVersion} onEnterComparisonMode={onEnterComparisonMode} />
          </View>
        )}

        {showInactiveToggle && (
          <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS), marginTop: scale(24) }]}>
            <ActionButton
              {...{
                C,
                loading: false,
                text: inactiveToggleText,
                borderTopRadius: true,
                borderBottomRadius: true,
                action: () => setShowOnlyActiveVersions?.((prev) => !prev),
              }}
            />
          </View>
        )}

        {onUploadNewVersion && (
          <View style={{ marginTop: scale(24), paddingHorizontal: scale(PADDING_HORIZONTAL_FOR_SHADOW) }}>
            <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              <ActionButton
                {...{
                  C,
                  loading: false,
                  text: `${i18n.t('Add version...')}`,
                  borderTopRadius: true,
                  borderBottomRadius: true,
                  action: onUploadNewVersion,
                  testID: TEST_IDS.ncFileVersions.addVersionButton,
                }}
              />
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default LeftColumn;
